import { QueryTable } from "@/components/query-table/QueryTable";
import PageHeader from "@/components/PageHeader";
import { Module } from "@/constants";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { DisplayProjectManager } from "@/types/projectManager";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { ProjectManagersDirectoryContent } from "@/modules/projects/components/managers/ProjectManagersDirectoryContent";
import { displayProjectManager } from "@/schemas/projectManager";
import { toCamelCase } from "@/lib/utils";
import { ProjectManagersAddUsersContextProvider } from "@/modules/projects/components/managers/ProjectManagersAddUsersContext";
import { ProjectManagersAddUsersDialog } from "@/modules/projects/components/managers/ProjectManagersAddUsersDialog";
import { ProjectManagersAddUsersButton } from "@/modules/projects/components/managers/ProjectManagersAddUsersButton";
import { ProjectManagersRemoveUsersButton } from "@/modules/projects/components/managers/ProjectManagersRemoveUsersButton";
import { ProjectManagersRemoveUsersContextProvider } from "@/modules/projects/components/managers/ProjectManagersRemoveUsersContext";
import { useProjectManagersQuery } from "@/modules/projects/hooks/useProjectManagersQuery";
import { useProjectManagersRemoveUsersForm } from "@/modules/projects/hooks/useProjectManagersRemoveUsersForm";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useRowSelectionContext } from "@/components/data-table/context/RowSelectionContext";
import { QueryKey } from "@tanstack/react-query";
import { UseInfiniteQueryResult, InfiniteData } from "@tanstack/react-query";
import { SuccessFetchListResponse } from "@/services/api";

interface ProjectManagersListContentProps {
  managersListQuery: UseInfiniteQueryResult<
    InfiniteData<SuccessFetchListResponse<DisplayProjectManager>>
  >;
  projectId: string;
  queryKey: QueryKey;
}

const ProjectManagersListContent = ({
  managersListQuery,
  projectId,
  queryKey,
}: ProjectManagersListContentProps) => {
  const { store } = useRowSelectionContext();
  const { handleSubmit, isSubmitting } = useProjectManagersRemoveUsersForm({
    store,
    projectId,
    queryKey,
  });

  const queryValue = useMemo(
    () => ({ query: managersListQuery }),
    [managersListQuery],
  );

  return (
    <ProjectManagersRemoveUsersContextProvider
      handleRemove={handleSubmit}
      isSubmitting={isSubmitting}
    >
      <DataTableQueryContextProvider value={queryValue}>
        <div className="flex flex-row gap-2 justify-between items-center py-2">
          <QueryTable.Menubar className="mb-0 grow">
            <QueryTable.Menubar.Columns />
            <QueryTable.Menubar.Settings />
          </QueryTable.Menubar>
          <div className="flex gap-2">
            <ProjectManagersRemoveUsersButton variant="outline" />
            <ProjectManagersAddUsersButton variant="outline" />
          </div>
        </div>
        <ProjectManagersDirectoryContent />
      </DataTableQueryContextProvider>
    </ProjectManagersRemoveUsersContextProvider>
  );
};

const ProjectManagersDirectoryComp = () => {
  const { t } = useTranslation();
  const name = toCamelCase(Module.Managers);
  const projectId = useProjectId();
  const projectQuery = useProjectQuery(projectId);
  const [isAddUsersDialogOpened, setIsAddUsersDialogOpened] = useState(false);
  const breadcrumbItems = useProjectBreadcrumb(projectQuery, [
    {
      label: t("projects.breadcrumb.managers"),
    },
  ]);

  const {
    query: managersListQuery,
    options,
    queryKey,
  } = useProjectManagersQuery({
    projectId,
    name,
  });

  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: true,
      enableFilters: false,
      enableSorting: true,
    }),
    [],
  );

  return (
    <ProjectManagersAddUsersContextProvider
      isAddUsersDialogOpened={isAddUsersDialogOpened}
      setIsAddUsersDialogOpened={setIsAddUsersDialogOpened}
      managersDirectoryQueryKey={queryKey}
    >
      <PageHeader
        breadcrumbItems={breadcrumbItems}
        title={t("projects.breadcrumb.managers")}
      />
      <ProjectDetailsAndNavigation />
      <RowSelectionProvider>
        <QueryTable<DisplayProjectManager>
          schema={displayProjectManager}
          name={name}
          query={managersListQuery}
          options={options}
          tableFeatures={tableFeatures}
        >
          <ProjectManagersListContent
            managersListQuery={managersListQuery}
            projectId={projectId}
            queryKey={queryKey}
          />
        </QueryTable>
      </RowSelectionProvider>
      <ProjectManagersAddUsersDialog />
    </ProjectManagersAddUsersContextProvider>
  );
};

export const ProjectManagersDirectory = memo(ProjectManagersDirectoryComp);
